// src/hooks/useTodos.js

import { useState, useEffect, useCallback } from 'react';
import { sortTodos } from '../utils/sortUtils'; 

const API_BASE_URL = 'https://taskmate-backend-3-vdb7.onrender.com/api/todos';

/**
 * Custom Hook zur Verwaltung der ToDo-Liste und der gesamten API-Kommunikation.
 * @param {string} sortCriterion - Das aktuelle Sortierkriterium ('priority', 'createdAt' oder 'none').
 * @returns {object} Enthält Zustände (todos, loading, error) und Funktionen für CRUD-Operationen.
 */
// ENTFERNT: isCategoryFeatureActive Parameter
export const useTodos = (sortCriterion) => {
    const [todos, setTodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- READ (Laden der Aufgaben beim Mount) ---
    const fetchTodos = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL);
            if (!response.ok) throw new Error('Netzwerkfehler beim Laden der Aufgaben.');
            const data = await response.json();
            // Sortiert die geladenen Daten nur, wenn sortCriterion nicht 'none' ist
            setTodos(sortCriterion === 'none' ? data : sortTodos(data, sortCriterion)); 
        } catch (err) {
            console.error(err);
            setError('Fehler beim Laden der Aufgaben. Läuft das Backend?');
        } finally {
            setLoading(false);
        }
    }, [sortCriterion]); 

    useEffect(() => {
        fetchTodos();
    }, [fetchTodos]); 

    // --- Sortierungseffekt (Reagiert auf Änderung des Sortierkriteriums) ---
    useEffect(() => {
        // Sortiert die aktuelle Liste nur neu, wenn das Kriterium NICHT 'none' ist
        if (sortCriterion !== 'none') {
            setTodos(prev => sortTodos(prev, sortCriterion));
        }
    }, [sortCriterion]);


    // --- CREATE (Aufgabe hinzufügen) ---
    const handleAddTask = useCallback(async (text, priority) => {
        // KORREKTUR: Kategorie wird NICHT mehr gesendet
        // Das 'order'-Feld wird beim Erstellen NICHT mehr gesetzt.
        const newTaskData = { description: text, priority, completed: false };
        
        try {
            const response = await fetch(API_BASE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newTaskData),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Fehler beim Hinzufügen');
            }
            const addedTask = await response.json();
            
            // Füge die neue Aufgabe hinzu und sortiere die gesamte Liste neu (sortTodos handhabt 'none')
            setTodos(prev => sortTodos([...prev, addedTask], sortCriterion));
            
            return true; 
        } catch (err) {
            console.error(err);
            throw new Error(`Fehler: ${err.message}`); 
        }
    }, [sortCriterion]); // ENTFERNT: isCategoryFeatureActive aus der Dependency-Liste


    // --- TOGGLE COMPLETED (Status ändern) ---
    const handleToggleCompleted = useCallback(async (id, currentStatus) => {
        try {
            const response = await fetch(`${API_BASE_URL}/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ completed: !currentStatus }),
            });
            if (!response.ok) throw new Error('Fehler beim Aktualisieren.');
            const updatedTask = await response.json();
            
            // Ersetze den Task und sortiere neu (sortTodos handhabt 'none')
            setTodos(prev => sortTodos(prev.map(todo => todo._id === id ? updatedTask : todo), sortCriterion));
            
            return true;
        } catch (err) {
            console.error(err);
            throw new Error('Fehler: Status konnte nicht geändert werden.');
        }
    }, [sortCriterion]);


    // --- UPDATE (Bearbeiten einer Aufgabe) ---
    const handleUpdateTask = useCallback(async (id, updatedData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description: updatedData.description,
                    priority: updatedData.priority
                }),
            });
            if (!response.ok) throw new Error('Fehler beim Aktualisieren der Aufgabe.');
            const updatedTask = await response.json();
            
            // Ersetze den Task und sortiere neu (sortTodos handhabt 'none')
            setTodos(prev => sortTodos(prev.map(todo => todo._id === id ? updatedTask : todo), sortCriterion));
            
            return true;
        } catch (err) {
            console.error(err);
            throw new Error('Fehler: Aufgabe konnte nicht aktualisiert werden.');
        }
    }, [sortCriterion]);


    // --- DELETE (Aufgabe löschen) ---
    const handleDeleteTask = useCallback(async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/${id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error('Fehler beim Löschen.');
            
            // Entferne den Task aus der Liste
            setTodos(prev => prev.filter(todo => todo._id !== id));
            
            return true;
        } catch (err) {
            console.error(err);
            throw new Error('Fehler: Aufgabe konnte nicht gelöscht werden.');
        }
    }, []);
    
    
    // ANPASSUNG: --- REORDER (Drag & Drop) Funktion wurde entfernt. ---


    return {
        todos,
        loading,
        error,
        handleAddTask,
        handleDeleteTask,
        handleToggleCompleted,
        handleUpdateTask,
        // handleReorder wurde entfernt
    };
};